import { ExternalLink } from 'lucide-react';
import { Badge } from '../primitives/Badge';
import { cn } from '../lib/cn';

/**
 * Citation — the four-part visual contract.
 *
 * The shape reflects the brand thesis: every SICA answer carries a textual
 * excerpt anchored to a specific document, page, and jurisdiction. The four
 * parts are mandatory wherever a citation is shown (visual-identity.md §5.3,
 * voice-and-vocabulary.md §5).
 */
export type Citation = {
  excerpt: string;
  document: string;
  /**
   * Page reference. Accepts either a single number (`27`, rendered as
   * "pág. 27") or a string for ranges or non-numeric references
   * (`'45-49'`, rendered as "pág. 45-49"). The string form is permitted
   * because real curricular documents often cite multi-page passages.
   */
  page: number | string;
  jurisdiction: string;
  /**
   * Optional URL to the source document. When present and `showSourceLink`
   * is true on the block, a "Ver fuente" link is rendered. When absent, the
   * link is rendered as a stub (with `aria-label` describing the destination)
   * to preserve the visual contract until the backend exposes real URLs.
   */
  sourceUrl?: string;
};

/**
 * CitationBlock — the brand component.
 *
 * Renders the four mandatory parts: gold quote glyph, italic Fraunces excerpt,
 * source line (document + page + jurisdiction badge), and the source link.
 * The visual treatment is enforced by the `.citation-block` class in
 * `@sica/ui/index.css`. Do not inline these styles in components.
 */
export function CitationBlock({
  citation,
  showSourceLink = true,
  className,
}: {
  citation: Citation;
  showSourceLink?: boolean;
  className?: string;
}) {
  const linkProps = citation.sourceUrl
    ? { href: citation.sourceUrl, target: '_blank' as const, rel: 'noopener noreferrer' }
    : { href: '#', onClick: (e: React.MouseEvent) => e.preventDefault() };

  return (
    <figure className={cn('citation-block', className)}>
      <span aria-hidden="true" className="block font-serif text-3xl leading-none text-accent-quote">
        “
      </span>
      <blockquote>
        <p>{citation.excerpt}</p>
      </blockquote>
      <figcaption className="citation-source flex flex-wrap items-center gap-2">
        <cite className="not-italic">
          <span className="text-ink">{citation.document}</span>
          <span className="mx-2 text-ink-subtle" aria-hidden="true">
            ·
          </span>
          <span>pág. {citation.page}</span>
        </cite>
        <Badge tone="accent">{citation.jurisdiction}</Badge>
        {showSourceLink && (
          <a
            {...linkProps}
            className="focus-ring ml-auto inline-flex items-center gap-1 rounded text-accent hover:underline"
            aria-label={`Ver fuente: ${citation.document}, página ${citation.page}`}
          >
            Ver fuente
            <ExternalLink size={12} />
          </a>
        )}
      </figcaption>
    </figure>
  );
}

/**
 * NoSourceState — the honest-empty variant.
 *
 * Rendered when SICA cannot answer because no citable source exists. Calm
 * typographic treatment, dashed border, italic Fraunces. No red, no error
 * icon (visual-identity.md §5.4). The absence of a citation is a feature,
 * not a fallback.
 */
export function NoSourceState({ prompt }: { prompt: string }) {
  return (
    <figure className="rounded-md border border-line border-dashed bg-paper p-5">
      <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
        Sin fuente
      </p>
      <p className="mt-3 font-serif italic text-ink">
        SICA no encontró un documento curricular argentino que responda esta pregunta.
      </p>
      <p className="mt-2 text-sm text-ink-muted">
        “{prompt}” no es una pregunta sobre normativa curricular. SICA no responde sin cita —
        ni siquiera cuando la respuesta podría parecer obvia.
      </p>
    </figure>
  );
}
